/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react'
import styled from '@emotion/styled'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { DocumentTemplate, GenerateDocumentModalProps, } from '../../../types/document-generation.types'
import config from 'config/app'
import { useLazyGetAssetsListQuery } from '../../../store/services/labAssets'
import { toast } from 'react-toastify'
import { OTHER_DOCUMENT_TEMPLATE_CATEGORY } from 'constants/documentTemplateCategories'
import { ORDER_INFORMATION_FIELD_KEYS } from 'constants/pdfFormFields'
import { getFilledPdfFields, getTemplatePdfUrl } from 'helpers/pdfFormFields'
import { PdfFormPanel } from 'components/molecules/PdfFormPanel'

const ModalContent = styled.div`
  display: flex;
  height: calc(90vh - 155px);
  gap: 1rem;
`

const LeftPanel = styled.div`
  width: 300px;
  border-right: 1px solid #e5e7eb;
  padding-right: 1rem;
  overflow-y: auto;
`

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2c83bd;
    box-shadow: 0 0 0 3px rgba(44, 131, 189, 0.1);
  }
`

const TemplateList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const TemplateItem = styled.div<{ selected: boolean }>`
  padding: 0.75rem;
  cursor: pointer;
  border-radius: 4px;
  font-size: 14px;
  background-color: ${(props) => (props.selected ? '#2c83bd' : 'transparent')};
  color: ${(props) => (props.selected ? 'white' : '#374151')};
  transition: all 0.15s ease;

  &:hover {
    background-color: ${(props) => (props.selected ? '#2c83bd' : '#f3f4f6')};
  }
`

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const CategoryText = styled.div`
  flex: 1;
  min-width: 0;
`

const CategoryName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  line-height: 1.3;
`

const CategoryCount = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const CategoryItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;

  .category-chevron {
    color: #d1d5db;
    font-size: 12px;
    transition: all 0.15s ease;
  }

  &:hover {
    border-color: #2c83bd;
    background-color: #f8fbfd;
    box-shadow: 0 1px 3px rgba(44, 131, 189, 0.12);

    .category-chevron {
      color: #2c83bd;
      transform: translateX(2px);
    }
  }
`

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.75rem;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`

const BackButton = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: white;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #2c83bd;
    color: #2c83bd;
    background-color: #f8fbfd;
  }
`

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`


// Templates whose checkbox fields are exposed for manual entry. Other templates
// keep their checkboxes hidden for now.
const CHECKBOX_ENABLED_TEMPLATES = ['HST Exclusion.pdf', 'Device Complaint Form.pdf']

const isSameTemplate = (a: DocumentTemplate, b: DocumentTemplate) =>
  a.folder === b.folder && a.fileName === b.fileName

export const GenerateDocumentModal = ({
  open,
  onClose,
  onGenerate,
  refetchOrder,
  order,
}: GenerateDocumentModalProps) => {
  const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplate | null>(null)
  const [isGenerating, setIsGenerating] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [manualFields, setManualFields] = useState<Record<string, string>>({})
  const pollingRef = useRef<NodeJS.Timeout | null>(null)
  const expectedFileRef = useRef<string | null>(null)

  const [getAssetsList, { data: templatesData, isLoading: isLoadingTemplates }] = useLazyGetAssetsListQuery()

  // Fetch templates from API when modal opens
  useEffect(() => {
    if (open) {
      const bucket = config?.aws?.assetsBucket
      const folder = config?.aws?.documentTemplatesFolder
      getAssetsList({ bucket, folder })
    } else {
      setSelectedTemplate(null)
      setSearchTerm('')
      setSelectedCategory(null)
      setManualFields({})
    }
  }, [open, getAssetsList])

  // Track previous template to detect actual template changes
  const prevTemplateRef = useRef<DocumentTemplate | null>(null)

  useEffect(() => {
    if (selectedTemplate && open) {
      // Only reset fields when template actually changes (not just when selectedTemplate is set)
      if (!prevTemplateRef.current || !isSameTemplate(prevTemplateRef.current, selectedTemplate)) {
        setManualFields({})
        prevTemplateRef.current = selectedTemplate
      }
    } else if (!open) {
      prevTemplateRef.current = null
    }
  }, [selectedTemplate, open])

  // API returns paths relative to the templates folder; the subfolder is the category
  const templates = useMemo((): DocumentTemplate[] => {
    if (!templatesData?.filenames) {
      return []
    }
    const rootFolder = config?.aws?.documentTemplatesFolder ?? ''
    return templatesData.filenames.map((path: string) => {
      const separatorIndex = path.lastIndexOf('/')
      const subfolder = path.slice(0, Math.max(separatorIndex, 0))
      const fileName = path.slice(separatorIndex + 1)
      return {
        name: fileName.endsWith('.pdf') ? fileName.slice(0, -4) : fileName,
        fileName,
        folder: subfolder ? `${rootFolder}/${subfolder}` : rootFolder,
        category: subfolder || OTHER_DOCUMENT_TEMPLATE_CATEGORY,
      }
    })
  }, [templatesData])

  // Alphabetical, with Other last
  const templatesByCategory = useMemo(() => {
    const categoryNames = Array.from(new Set(templates.map(({ category }) => category))).sort((a, b) =>
      Number(a === OTHER_DOCUMENT_TEMPLATE_CATEGORY) - Number(b === OTHER_DOCUMENT_TEMPLATE_CATEGORY) ||
      a.localeCompare(b),
    )
    return categoryNames.map((name) => ({
      name,
      templates: templates.filter(({ category }) => category === name),
    }))
  }, [templates])

  const openedCategory = searchTerm ? undefined : templatesByCategory.find(({ name }) => name === selectedCategory)

  // Search runs across all categories; otherwise list the opened category
  const filteredTemplates = searchTerm
    ? templates.filter((template) =>
      template.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : openedCategory?.templates ?? []
  const showCategories = !searchTerm && !selectedCategory

  const formatTemplatesCount = (count: number) => `${count} ${count === 1 ? 'Template' : 'Templates'}`

  const handleGenerate = async () => {
    if (!selectedTemplate) return

    setIsGenerating(true)
    try {
      if (onGenerate) {
        const filledFields = getFilledPdfFields(manualFields)

        // Only pass fields if there's at least one field filled
        const hasFields = Object.keys(filledFields).length > 0
        await onGenerate(selectedTemplate, hasFields ? filledFields : undefined)

        // Start polling for the generated file
        if (refetchOrder) {
          // Clear any existing polling
          if (pollingRef.current) {
            clearInterval(pollingRef.current)
          }

          expectedFileRef.current = selectedTemplate.fileName.replace(/\.pdf$/i, '')
          let attempts = 0
          const maxAttempts = 5

          pollingRef.current = setInterval(() => {
            attempts++
            refetchOrder()

            if (attempts >= maxAttempts) {
              clearInterval(pollingRef.current!)
              pollingRef.current = null
              expectedFileRef.current = null
              toast.warning('Document generation may still be in progress. Please refresh page to see the file.')
            }
          }, 3500)
        }
      }

      setTimeout(() => {
        onClose()
      }, 1000)
    } catch (error) {
      console.error('Error generating document:', error)
      expectedFileRef.current = null
    } finally {
      setIsGenerating(false)
    }
  }

  // Check for the expected file when order updates
  useEffect(() => {
    if (!expectedFileRef.current || !order?.files || !refetchOrder) return

    const fileExists = Object.values(order.files).some(
      (file: any) => file.uploadType === 'OTHER' && file.filename?.includes(expectedFileRef.current)
    )

    if (fileExists) {
      clearInterval(pollingRef.current!)
      pollingRef.current = null
      expectedFileRef.current = null
      toast.success('Document generated and attached successfully!')
    }
  }, [order, refetchOrder])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [])

  const handlePdfFieldChange = (fieldName: string, value: string) => {
    setManualFields((prev) => ({
      ...prev,
      [fieldName]: value,
    }))
  }

  const checkboxesEnabled = CHECKBOX_ENABLED_TEMPLATES.some(
    (fileName) => fileName.toLowerCase() === selectedTemplate?.fileName.toLowerCase(),
  )

  const handleSelectTemplate = (template: DocumentTemplate) => {
    setSelectedTemplate(template)
  }

  return (
    <Modal
      show={open}
      onHide={onClose}
      size="xl"
      centered
      dialogClassName="modal-80h">
      <Modal.Header>
        <Modal.Title>Generate Document</Modal.Title>
        <button
          type="button"
          className="close"
          aria-label="Close"
          onClick={onClose}
        >
          <span
              css={{fontSize: '1.5rem'}}
              aria-hidden="true"
          >
            &times;
          </span>
        </button>
      </Modal.Header>

      <Modal.Body css={{ padding: '1rem' }}>
        <ModalContent>
          <LeftPanel>
            <div css={{ marginBottom: '1rem' }}>
              <label css={{ fontWeight: 600, fontSize: '14px', color: '#374151', display: 'block', marginBottom: '0.5rem' }}>
                Search:
              </label>
              <SearchInput
                type="text"
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {openedCategory ? (
              <CategoryHeader>
                <BackButton type="button" title="All categories" onClick={() => setSelectedCategory(null)}>
                  <i className="fas fa-chevron-left"></i>
                </BackButton>
                <CategoryText>
                  <CategoryName>{openedCategory.name}</CategoryName>
                  <CategoryCount>{formatTemplatesCount(openedCategory.templates.length)}</CategoryCount>
                </CategoryText>
              </CategoryHeader>
            ) : (
              <div css={{ marginBottom: '0.5rem', fontSize: '12px', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>
                {searchTerm ? formatTemplatesCount(filteredTemplates.length) : 'Categories'}
              </div>
            )}

            {isLoadingTemplates ? (
              <div css={{ padding: '2rem 1rem', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
                <i className="fas fa-spinner fa-spin" css={{ fontSize: '2rem', marginBottom: '0.5rem' }}></i>
                <div>Loading templates...</div>
              </div>
            ) : showCategories ? (
              <CategoryList>
                {templatesByCategory.map((category) => (
                  <CategoryItem key={category.name} onClick={() => setSelectedCategory(category.name)}>
                    <CategoryText>
                      <CategoryName>{category.name}</CategoryName>
                      <CategoryCount>{formatTemplatesCount(category.templates.length)}</CategoryCount>
                    </CategoryText>
                    <i className="fas fa-chevron-right category-chevron"></i>
                  </CategoryItem>
                ))}
              </CategoryList>
            ) : (
              <>
                <TemplateList>
                  {filteredTemplates.map((template, index) => (
                    <TemplateItem
                      key={index}
                      selected={!!selectedTemplate && isSameTemplate(selectedTemplate, template)}
                      onClick={() => handleSelectTemplate(template)}>
                      <div css={{ fontWeight: 500 }}>{template.name}</div>
                    </TemplateItem>
                  ))}
                </TemplateList>

                {filteredTemplates.length === 0 && (
                  <div css={{ padding: '2rem 1rem', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
                    <i className="fas fa-search" css={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.5 }}></i>
                    <div>No templates found</div>
                  </div>
                )}
              </>
            )}
          </LeftPanel>

          <RightPanel>
            {selectedTemplate ? (
              <PdfFormPanel
                key={`${selectedTemplate.folder}/${selectedTemplate.fileName}`}
                pdfUrl={getTemplatePdfUrl(selectedTemplate)}
                title={selectedTemplate.name}
                autoFilledKeys={ORDER_INFORMATION_FIELD_KEYS}
                showCheckboxes={checkboxesEnabled}
                values={manualFields}
                onChange={handlePdfFieldChange}
                fieldsTabLabel="Manual Information Enter"
                fieldsHint="These are empty form fields from the PDF that are not covered by the standard order information."
              />
            ) : (
              <div
                css={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  textAlign: 'center',
                  color: '#9ca3af',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '2px dashed #e5e7eb',
                }}>
                <div>
                  <i className="fas fa-file-pdf" css={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
                  <div css={{ fontSize: '16px' }}>Select a category and a document template to preview</div>
                </div>
              </div>
            )}
          </RightPanel>
        </ModalContent>
      </Modal.Body>

      <Modal.Footer>
        <div
          css={{
            flex: 1,
            fontSize: '14px',
            color: '#6b7280',
            display: 'flex',
            alignItems: 'center',
          }}>
          {selectedTemplate && (
            <>
              <i className="fas fa-info-circle" css={{ marginRight: '0.5rem' }}></i>
              This form will be populated with order #{order.id} information
            </>
          )}
        </div>
        <Button variant="outline-secondary" onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
        {selectedTemplate && (
          <Button
            variant="primary"
            onClick={handleGenerate}
            disabled={isGenerating}>
            {isGenerating ? (
              <>
                <i className="fas fa-spinner fa-spin" css={{ marginRight: '0.5rem' }}></i>
                Generating...
              </>
            ) : (
              <>
                <i className="fas fa-file-medical" css={{ marginRight: '0.5rem' }}></i>
                Generate Document
              </>
            )}
          </Button>
        )}
      </Modal.Footer>

      <style>{`
        .modal-80h .modal-content {
          height: 90vh;
          display: flex;
          flex-direction: column;
        }
        .modal-80h .modal-body {
          flex: 1;
          overflow: hidden;
        }
      `}</style>
    </Modal>
  )
}

